import { useQuery } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiRequest } from '@/lib/query-client';

/**
 * The owner skipped Gmail in the onboarding wizard and has not connected an inbox since. Their dashboard reminds them
 * instead of opening the connect dialog on every visit. Only a firm created by the wizard has an onboarding status
 * worth asking about, so the query is off for everyone else.
 */
export function useGmailReminder(enabled: boolean): { skippedGmail: boolean; reminderLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['/api/onboarding/status'],
    queryFn: async () => (await apiRequest('GET', '/api/onboarding/status')).json(),
    enabled,
  });
  const gmail = data?.wizard?.gmail;
  return { skippedGmail: !!gmail && gmail.skipped === true && gmail.connected !== true, reminderLoading: enabled && isLoading };
}

export function GmailReminderBanner({ onConnect }: { onConnect: () => void }) {
  return (
    <div
      className="flex flex-col gap-3 rounded-md border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between"
      data-testid="banner-gmail-reminder"
    >
      <div className="flex items-start gap-3 text-sm">
        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
        <p>
          <span className="font-medium">Connect Gmail to bring in your first claim.</span>{' '}
          <span className="text-muted-foreground">
            ClaimHive reads your inbox to spot claim emails. You can disconnect it any time in Settings.
          </span>
        </p>
      </div>
      <Button size="sm" onClick={onConnect} data-testid="button-gmail-reminder-connect">
        Connect Gmail
      </Button>
    </div>
  );
}
